/**
 * M2 P2: station meshes + floating-origin placement. Mirrors BodyRenderer:
 * each station's authoritative position is Float64 (host body world pos + spec
 * offset); the render position is the Float32 delta from the ship's world pos so
 * the ship stays at the render origin. GLB load reuses `normaliseToRadius` (the
 * shared body/ship factory helper); a torus fallback keeps stations visible
 * before the creative squad delivers final art, so missing GLBs never block.
 *
 * Distance-based LOD (perf): full GLB shown when near, cheap proxy torus beyond
 * FULL_STATION_KM, both hidden beyond PROXY_STATION_KM.  Thresholds live in
 * render/lod.ts.  The missing-GLB fallback torus is preserved as-is (it acts as
 * both fallback AND near-view mesh when the GLB is absent).
 */
import * as THREE from 'three';
import { assetDir } from './assetDir.js';
import type { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { createGltfLoader } from './gltf.js';
import { add, type Vec3, type StationSpec } from '@blackmarble/sim-core';
import { normaliseToRadius, enableShadows } from './bodyFactory.js';
import { applyLod, createGlintSprite, FULL_STATION_KM, PROXY_STATION_KM } from './lod.js';
import type { World } from '../sim/world.js';

const STATION_DISPLAY_KM = 8; // render half-extent in sim units (km)

/** Per-station LOD state. */
interface StationLod {
  group: THREE.Group;
  /** The loaded GLB scene, or null when the GLB hasn't arrived / failed. */
  glbScene: THREE.Object3D | null;
  /** Cheap proxy torus — always present, sized to display radius. */
  proxy: THREE.Mesh;
  /** Far-LOD sun glint so the station never vanish-pops past the proxy range. */
  glint: THREE.Sprite;
}

export class StationRenderer {
  private readonly lods = new Map<string, StationLod>();

  constructor(
    private readonly stations: readonly StationSpec[],
    private readonly parent: THREE.Group,
  ) {}

  async loadAll(baseUrl = assetDir('stations')): Promise<void> {
    const loader = createGltfLoader();
    for (const spec of this.stations) {
      const group = new THREE.Group();
      group.name = `station:${spec.id}`;
      const proxy = makeStationProxy();
      proxy.castShadow = true; // WAVE 2a: fallback torus still self-shadows
      proxy.receiveShadow = true;
      group.add(proxy);
      const glint = createGlintSprite();
      group.add(glint);
      const glbScene = await loadStationGlb(loader, spec, baseUrl);
      if (glbScene !== null) {
        enableShadows(glbScene); // WAVE 2a: stations cast onto docked ships + each other
        group.add(glbScene);
        proxy.visible = false; // start hidden; LOD will manage each frame
      }
      // When GLB failed: proxy remains visible and acts as the fallback mesh.
      this.parent.add(group);
      this.lods.set(spec.id, { group, glbScene, proxy, glint });
    }
  }

  /** Resolve a station's current Float64 world position (host body + offset). */
  worldPos(spec: StationSpec, world: World): Vec3 {
    const host = world.get(spec.hostBody);
    return add(host ? host.worldPos : { x: 0, y: 0, z: 0 }, spec.offset);
  }

  /** Floating-origin placement + per-station LOD toggle. */
  update(shipWorldPos: Vec3, world: World, t: number): void {
    for (const spec of this.stations) {
      const lod = this.lods.get(spec.id);
      if (!lod) continue;
      const wp = this.worldPos(spec, world);
      lod.group.position.set(
        wp.x - shipWorldPos.x,
        wp.y - shipWorldPos.y,
        wp.z - shipWorldPos.z,
      );
      lod.group.rotation.y = t * 0.05; // slow station spin

      if (lod.glbScene !== null) {
        // GLB present: normal LOD path — full vs proxy vs hidden + far glint.
        applyLod(
          lod.group.position, lod.glbScene, lod.proxy,
          FULL_STATION_KM, PROXY_STATION_KM, lod.glint,
        );
      } else {
        // GLB absent: proxy is the only mesh and stays visible at any distance;
        // the glint only lights up beyond the proxy range so it never double-draws.
        lod.glint.visible = false;
      }
    }
  }
}

/** Build a cheap proxy torus sized to the station display radius. */
function makeStationProxy(): THREE.Mesh {
  const geo = new THREE.TorusGeometry(STATION_DISPLAY_KM, STATION_DISPLAY_KM * 0.28, 6, 12);
  const mat = new THREE.MeshStandardMaterial({ color: 0x8899aa, roughness: 0.6, metalness: 0.3 });
  return new THREE.Mesh(geo, mat);
}

/** Load one station GLB; returns the scene on success or null on failure. */
async function loadStationGlb(
  loader: GLTFLoader,
  spec: StationSpec,
  baseUrl: string,
): Promise<THREE.Object3D | null> {
  try {
    const gltf = await loader.loadAsync(`${baseUrl}${spec.id}.glb`);
    normaliseToRadius(gltf.scene, STATION_DISPLAY_KM);
    return gltf.scene;
  } catch {
    return null;
  }
}
